import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Share2 } from "lucide-react";
import { Footer } from "../components/Footer";
import { NewsletterForm } from "../components/NewsletterForm";
import { Reveal } from "../components/Reveal";
import { WastelandTrophies } from "../components/WastelandTrophies";
import { toast } from "../components/ui/sonner";
import { downloadShareCard } from "../utils/shareCard";
import {
  adoptCloudPoints,
  fetchCrowHunters,
  fetchTopRunners,
  getLinkedTag,
  getLocalPlays,
  getLocalPoints,
  mintTag,
  setLinkedTag,
  getDailyStreak,
  syncDailyFromCloud,
  syncPointsToTag,
} from "../utils/rewards";

const EARN_RULES = [
  {
    game: "WASTELAND RUN",
    rule: "2 PTS every run + 1 PT per 150 signal — up to 25 PTS a run.",
    link: "/wasteland-run",
    cta: "RUN NOW →",
  },
  {
    game: "AL RIOTBALL",
    rule: "2 PTS every match + 2 PTS per goal — up to 40 PTS a match.",
    link: "/riotball",
    cta: "PLAY NOW →",
  },
  {
    game: "DOLO'S NIGHT",
    rule: "1 PT per 3 supplies scavenged + 5 PTS for escaping a side door — +1 PT for every rampage kill.",
    link: "/side-games#dolos-night",
    cta: "SURVIVE →",
  },
  {
    game: "APOCALYPSE BOARD",
    rule: "3 PTS for finishing a table — 10 PTS if you take the crown.",
    link: "/wasteland-radio",
    cta: "HIT THE TABLE →",
  },
  {
    game: "DOLOPALS",
    rule: "1 PT every unbox — 5 PTS when a Legendary drops.",
    link: "/dolopals",
    cta: "UNBOX →",
  },
  {
    game: "DAILY RATION",
    rule: "+15 PTS every day — crack the free Bio Hack tube. Consecutive days stack a +5 streak bonus (up to +45 PTS).",
    link: "/riotball",
    cta: "SEE BIO HACK →",
  },
];

const badgeTier = (b) => (b.startsWith("30") ? "is-legend" : b.startsWith("14") ? "is-elite" : "");

export const TIERS = [
  { pts: 250, title: "5% OFF MERCH", note: "any order on the store", code: "DOLO5" },
  { pts: 750, title: "10% OFF MERCH", note: "plus early drop windows", code: "DOLO10" },
  { pts: 1500, title: "15% OFF + FIRST DROP ALERTS", note: "new chapters, TCG waves, radio exclusives", code: "DOLO15" },
  { pts: 3000, title: "SIGNED ART PRINT + 20% OFF", note: "autographed by the creator, shipped with your order", code: "DOLO20" },
  { pts: 5000, title: "GIFT CRATE + EVENT INVITES", note: "mystery merch crate and first call on live events", code: "DOLO25" },
  { pts: 10000, title: "RADIOACTIVE DUST — DOLO SOUNDTRACK", note: "CD cover release unlocks free on the radio page", code: "DUST10K", link: "/wasteland-radio#radioactive-dust-drop", cta: "UNLOCK ON RADIO →" },
];

export default function Rewards() {
  const [points, setPoints] = useState(() => getLocalPoints());
  const [tag, setTag] = useState(() => getLinkedTag() || "");
  const [tagInput, setTagInput] = useState("");
  const [tagStatus, setTagStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [top, setTop] = useState([]);
  const [crowHunters, setCrowHunters] = useState([]);
  const [badges, setBadges] = useState([]);
  const [plays, setPlays] = useState(() => getLocalPlays());

  useEffect(() => {
    const onEarn = (event) => {
      setPoints(event.detail.total);
      setPlays(getLocalPlays());
    };
    window.addEventListener("dolo-rewards", onEarn);
    const linked = getLinkedTag();
    if (linked) {
      adoptCloudPoints(linked)
        .then((d) => {
          setPoints(Math.max(getLocalPoints(), d.points || 0));
          setBadges(d.badges || []);
        })
        .catch(() => {});
      syncDailyFromCloud(linked);
    }
    fetchTopRunners().then(setTop).catch(() => {});
    fetchCrowHunters().then(setCrowHunters).catch(() => {});
    return () => window.removeEventListener("dolo-rewards", onEarn);
  }, []);

  const linkTag = async (raw) => {
    const clean = raw.toUpperCase().trim();
    if (!clean) return;
    setBusy(true);
    setTagStatus("");
    try {
      // pull the vault first so a richer cloud balance is never overwritten, then push up — the backend keeps the max either way
      const data = await adoptCloudPoints(clean);
      setBadges(data.badges || []);
      setLinkedTag(clean);
      setTag(clean);
      await syncPointsToTag(clean);
      setPoints(Math.max(getLocalPoints(), data.points || 0));
      setTagStatus(`TAG ${clean} LINKED — your stash is vaulted and syncs across devices.`);
      toast.success("TITAN TAG LINKED — POINTS VAULTED.");
      fetchTopRunners().then(setTop).catch(() => {});
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === "string" ? detail : "TAG REJECTED — CHECK THE SCRATCHES.";
      setTagStatus(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const mint = async () => {
    setBusy(true);
    setTagStatus("");
    try {
      const fresh = await mintTag();
      setTag(fresh);
      await syncPointsToTag(fresh);
      setTagStatus(`TAG ${fresh} MINTED — scratch it somewhere safe. Your current stash is already vaulted.`);
      toast.success("NEW TITAN TAG MINTED.");
    } catch {
      setTagStatus("TAG FOUNDRY JAMMED — RETRY.");
      toast.error("TAG FOUNDRY JAMMED — RETRY.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="rewards-page" data-testid="rewards-page">
      <section className="rewards-hero">
        <span className="rewards-eyebrow">LOYALTY TRANSMISSION // ALL RUNNERS</span>
        <h1>
          DOLO <em>REWARDS</em>
        </h1>
        <p>
          The more you play, the more the wasteland pays. Every run, match, table, and unbox stacks points toward merch
          discounts, first alerts on drops, autographed art from the creator, gift crates, and event invites.
        </p>
      </section>

      <Reveal className="rewards-balance-card" data-testid="rewards-balance-card">
        <span>YOUR STASH</span>
        <strong data-testid="rewards-balance-total">{points} PTS</strong>
        <span className="rewards-balance-tag" data-testid="rewards-balance-tag">
          {tag ? `VAULTED UNDER TAG ${tag}` : "LOCAL ONLY — LINK A TITAN TAG BELOW TO SYNC ACROSS DEVICES"}
        </span>
        <span data-testid="rewards-plays-count">
          GAMES PLAYED: {Object.values(plays).reduce((a, b) => a + b, 0)}
        </span>
        <span data-testid="rewards-streak-count">DAILY STREAK: {getDailyStreak()} DAYS</span>
        {badges.length > 0 ? (
          <span className="rewards-balance-badges" data-testid="rewards-balance-badges">
            {badges.map((b) => (
              <b key={b}>{b}</b>
            ))}
          </span>
        ) : null}
      </Reveal>

      <section className="rewards-section" data-testid="rewards-earn-section">
        <h2>HOW TO EARN</h2>
        <p>Points land the moment a game ends. No signup needed — your device banks them instantly.</p>
        <div className="rewards-earn-grid">
          {EARN_RULES.map((r) => (
            <div className="rewards-earn-card" key={r.game} data-testid={`earn-card-${r.game.toLowerCase().replaceAll(" ", "-")}`}>
              <h3>{r.game}</h3>
              <p>{r.rule}</p>
              <Link to={r.link}>{r.cta}</Link>
            </div>
          ))}
        </div>
      </section>

      <section className="rewards-section" data-testid="rewards-tiers-section">
        <h2>REWARD TIERS</h2>
        <p>Hit a tier and it stays unlocked. Store discounts apply at igotism.com — and 10,000+ PTS unlocks the Radioactive Dust soundtrack cover on Wasteland Radio.</p>
        {TIERS.map((tier) => {
          const unlocked = points >= tier.pts;
          return (
            <div
              className={`rewards-tier ${unlocked ? "is-unlocked" : ""}`}
              key={tier.pts}
              data-testid={`tier-${tier.pts}`}
            >
              <span className="rewards-tier-cost">{tier.pts.toLocaleString()}{tier.pts === 10000 ? "+" : ""} PTS</span>
              <div className="rewards-tier-info">
                <strong>{tier.title}</strong>
                <span>{tier.note}</span>
                {tier.link ? (
                  <Link className="rewards-tier-code rewards-tier-link" to={tier.link} data-testid={`tier-${tier.pts}-link`}>
                    {tier.cta}
                  </Link>
                ) : (
                  <span className="rewards-tier-code">REF CODE: {tier.code}</span>
                )}
              </div>
              <span className="rewards-tier-state">{unlocked ? "UNLOCKED" : "LOCKED"}</span>
            </div>
          );
        })}
        <p className="rewards-note" data-testid="rewards-honor-note">
          BETA HONOR SYSTEM — while the vault wires into the store, screenshot your stash and quote your ref code when
          you order (or ping the crew through the comms channel). Your discount, signed art, or gift crate gets applied
          manually. Points are never deducted — tiers are permanent.
        </p>
      </section>

      <section className="rewards-section" data-testid="rewards-board-section">
        <h2>TOP RUNNERS</h2>
        <p>The most dedicated players on the mesh, ranked by vaulted points. Link a tag and climb.</p>
        <div className="rewards-board-list" data-testid="rewards-leaderboard">
          {top.length === 0 ? (
            <p className="rewards-board-empty" data-testid="rewards-leaderboard-empty">
              NO VAULTED RUNNERS YET — BE THE FIRST NAME ON THE BOARD.
            </p>
          ) : (
            top.map((row, i) => (
              <div className={`rewards-board-row ${i === 0 ? "is-leader" : ""}`} key={row.tag} data-testid={`leaderboard-row-${i}`}>
                <em>#{i + 1}</em>
                <span>
                  {row.name || row.tag}
                  {row.name ? <small className="rewards-board-tag">{row.tag}</small> : null}
                  {i === 0 ? (
                    <b className="rewards-leader-badge" data-testid="leader-badge">
                      <Crown size={12} /> RUNNER OF THE WEEK
                    </b>
                  ) : null}
                  {(row.badges || []).map((b) => (
                    <b className={`rewards-row-badge ${badgeTier(b)}`} key={b} data-testid={`badge-${b.toLowerCase().replaceAll(" ", "-")}`}>
                      {b}
                    </b>
                  ))}
                </span>
                <strong>{row.points} PTS</strong>
                {(row.badges || []).length > 0 ? (
                  <button
                    type="button"
                    className="share-card-btn"
                    title="Download hall-of-fame card"
                    onClick={() => downloadShareCard({ name: row.name || row.tag, tag: row.tag, points: row.points, badges: row.badges })}
                    data-testid={`share-card-${i}`}
                  >
                    <Share2 size={12} />
                  </button>
                ) : null}
              </div>
            ))
          )}
        </div>
      </section>

      <section className="rewards-section" data-testid="rewards-trophy-section">
        <h2>BOSS TROPHY WALL</h2>
        <p>The five named boss worms of Wasteland Run — 10,000 hull each. Break one in a duel and its trophy lights up here and on the run page.</p>
        <WastelandTrophies />
      </section>

      <section className="rewards-section" data-testid="rewards-crow-section">
        <h2>MOST CROWS FELLED</h2>
        <p>The sky hunters of Wasteland Run, ranked by total crows blasted or slashed out of the sky. Ember crows count double credits — but every feather here counts once.</p>
        <div className="rewards-board-list" data-testid="crow-hunter-leaderboard">
          {crowHunters.length === 0 ? (
            <p className="rewards-board-empty" data-testid="crow-hunter-empty">
              NO CROWS FELLED YET — THE FLOCK IS WAITING OVER THE HIGHWAY.
            </p>
          ) : (
            crowHunters.map((row, i) => (
              <div className={`rewards-board-row ${i === 0 ? "is-leader" : ""}`} key={row.tag} data-testid={`crow-hunter-row-${i}`}>
                <em>#{i + 1}</em>
                <span>
                  {row.name || row.tag}
                  {row.name ? <small className="rewards-board-tag">{row.tag}</small> : null}
                  {i === 0 ? (
                    <b className="rewards-leader-badge" data-testid="crow-leader-badge">
                      <Crown size={12} /> CROW HUNTER
                    </b>
                  ) : null}
                </span>
                <strong>{row.crow_kills} CROWS</strong>
              </div>
            ))
          )}
        </div>
      </section>

      <section className="rewards-section" data-testid="rewards-tag-section">
        <h2>VAULT YOUR POINTS</h2>
        <p>
          A Titan Tag banks your stash in the cloud vault — same code that syncs your DoloPals crew and board crowns.
          Lose the device, keep the points.
        </p>
        <div className="rewards-tag-box">
          <div className="rewards-tag-row">
            <input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              placeholder="ENTER YOUR TITAN TAG…"
              disabled={busy}
              data-testid="rewards-tag-input"
            />
            <button type="button" onClick={() => linkTag(tagInput)} disabled={busy} data-testid="rewards-tag-link-button">
              LINK TAG
            </button>
            <button type="button" onClick={mint} disabled={busy} data-testid="rewards-tag-mint-button">
              MINT A TAG
            </button>
          </div>
          <p className="rewards-tag-status" data-testid="rewards-tag-status">
            {tagStatus || "Minting is free. Write the tag down — it is the only key to your vault."}
          </p>
        </div>
      </section>

      <section className="rewards-section" data-testid="rewards-alerts-section">
        <Reveal className="rewards-alerts">
          <h3>FIRST DROP ALERTS</h3>
          <p>New chapters, merch drops, TCG waves, autograph windows, and event dates — subscribers hear it first.</p>
          <NewsletterForm />
        </Reveal>
      </section>

      <Footer />
    </main>
  );
}
